import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import moment from "moment";
import { useContext } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ActionItem, ActionItemType } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import OutreachContactDrawer from "features/Influencer/ContactList/OutreachContactDrawer";
import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import DefaultActivityCard from "./ActivityCard";
import ListSkeleton from "./ListSkeleton";
import styles from "./styles";

interface Props {
  fetchLoading: boolean;
}

const ActionItemList = ({ fetchLoading }: Props) => {
  const navigate = useNavigate();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { handleOpenQuickSendIndividual } = useContext(QuickSendDrawerContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);

  const handleArchive = async (e: CustomEvent, actionItem: ActionItem) => {
    e.stopPropagation();

    setActionItems((prev) => prev.filter((item) => item.id !== actionItem.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItem.id}/archive`,
        "POST",
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const openDrawer = (actionItem: ActionItem) => {
    trackEvent("Action Item Clicked", {
      "Action Item ID": actionItem.id,
      "Action Item Type": actionItem.type,
      "Outreach Contact ID": actionItem.outreachContactId,
    });
    if (
      actionItem.type === ActionItemType.new_contact_outreach &&
      actionItem?.outreachContact?.bentoBrandId
    ) {
      handleOpenQuickSendIndividual(
        actionItem?.outreachContact?.bentoBrandId,
        "Action Item",
        undefined,
        true,
      );
    } else {
      navigate(
        `action/${actionItem.outreachContactId}/email/${actionItem?.gmailThreadId}`,
      );
    }
  };

  const getDifference = (actionItem: ActionItem) => {
    const today = moment();
    const start =
      actionItem?.pendingAt ||
      actionItem?.outreachContact?.lastGmailMessageDate ||
      0;
    if (start === 0) return "";
    const lastEmailDate = moment(start * 1000);
    const duration = moment.duration(today.diff(lastEmailDate));
    const days = duration.asDays();

    if (days >= 30) {
      const month = duration.asMonths();

      return (
        `${month.toFixed(0)}` +
        (Number(month.toFixed(0)) < 2 ? ` month ago` : ` months ago`)
      );
    } else if (days >= 7) {
      const week = duration.asWeeks();
      return (
        `${week.toFixed(0)}` +
        (Number(week.toFixed(0)) < 2 ? ` week ago` : ` weeks ago`)
      );
    } else if (days >= 1) {
      return (
        `${days.toFixed(0)}` +
        (Number(days.toFixed(0)) < 2 ? ` day ago` : ` days ago`)
      );
    } else {
      return "";
    }
  };

  const buttonTitle = {
    [ActionItemType.past_partnership_check_in]: "Send Check-in",
    [ActionItemType.rate_discussion_follow_up]: "Send Follow-up",
    [ActionItemType.new_contact_outreach]: "Send New Pitch",
    [ActionItemType.brand_response]: "Reply",
    [ActionItemType.out_of_office_follow_up]: "Send Follow-up",
  };

  if (fetchLoading) {
    return <ListSkeleton />;
  }
  return (
    <Box sx={{ mb: 3 }}>
      {actionItems?.length === 0 && <DefaultActivityCard />}
      {actionItems?.map((actionItem) => (
        <Card elevation={2} sx={{ backgroundColor: "#E9F1EA", mb: 3 }}>
          <CardActionArea onClick={() => openDrawer(actionItem)}>
            <CardHeader
              title={
                <Box sx={{ fontSize: 14 }}>
                  {actionItem?.brandName} | {actionItem?.description}
                </Box>
              }
              subheader={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography sx={{ fontSize: 12 }}>
                    <strong>
                      {actionItem?.outreachContact?.lastGmailMessageSubject}
                    </strong>
                  </Typography>

                  <FormHelperText sx={{ color: grey[800], fontSize: 10 }}>
                    {formatEpochTimestamp({
                      epochTimestamp:
                        actionItem?.outreachContact?.lastGmailMessageDate || 0,
                      addYear: true,
                    })}{" "}
                  </FormHelperText>
                </Grid>
              }
              action={
                <IconButton
                  sx={styles.archiveButton}
                  onClick={(event) => handleArchive(event, actionItem)}
                >
                  <Box component="i" className="fa-regular fa-xmark" />
                </IconButton>
              }
            />

            <CardContent sx={{ mt: -3 }}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: 10, color: grey[500] }}>
                  {actionItem?.outreachContact?.lastGmailThreadParticipants}
                </Typography>

                <FormHelperText sx={{ color: "warning.main", fontSize: 10 }}>
                  {getDifference(actionItem)}
                </FormHelperText>
              </Grid>

              {actionItem?.outreachContact?.lastGmailMessageSnippet && (
                <Typography
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: `${actionItem?.outreachContact?.lastGmailMessageSnippet}...`,
                  }}
                  sx={styles.text}
                />
              )}

              <Button
                size="small"
                color="primary"
                variant="contained"
                sx={{ textTransform: "none", mt: 1, fontSize: 12 }}
                disableElevation
              >
                {buttonTitle[actionItem.type] || "Send Email"}
              </Button>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}

      <Routes>
        <Route
          path="action/:outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
      </Routes>
    </Box>
  );
};

export default ActionItemList;
